export interface SiteDeprecation {
  /** Specific sites being deprecated */
  sites: string[];
  /** Cluster prefix — banner shows for all sites in this cluster */
  cluster: string;
  /** Date to start showing the warning banner */
  warningDate: Date;
  /** Date to block the UI for the specific sites */
  cutoffDate: Date;
}

const BOOST_RECEIVE_LINK = 'https://boost.aws.a2z.com/elements/inventory/receive';

export const SITE_DEPRECATIONS: SiteDeprecation[] = [
  {
    sites: ['IAD057'],
    cluster: 'IAD',
    // Dates in UTC. Adjusted for EDT (UTC-4).
    // Warning: April 7, 2026 midnight EDT = 04:00Z
    // Cutoff: April 20, 2026 6:15 PM EDT = 22:15Z
    warningDate: new Date('2026-04-07T04:00:00Z'),
    cutoffDate: new Date('2026-04-20T22:15:00Z'),
  },
  {
    sites: ['IAD058'],
    cluster: 'IAD',
    // Dates in UTC. Adjusted for EDT (UTC-4).
    // Warning: May 15, 2026 midnight EDT = 04:00Z
    // Cutoff: May 18, 2026 6:15 PM EDT = 22:15Z
    warningDate: new Date('2026-05-15T04:00:00Z'),
    cutoffDate: new Date('2026-05-18T22:15:00Z'),
  },
];

export type DeprecationStatus = 'none' | 'warning' | 'blocked';

export interface SiteDeprecationResult {
  status: DeprecationStatus;
  dismissible: boolean;
  message: string;
  boostLink?: string;
}

const formatDate = (date: Date): string => {
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const buildWarningMessage = (dep: SiteDeprecation): string => {
  const siteList = dep.sites.join(' and ');
  const siteLabel = dep.sites.length === 1 ? 'site' : 'sites';
  const pronoun = dep.sites.length === 1 ? 'it migrates' : 'they migrate';
  return `Effective ${formatDate(dep.cutoffDate)}, Unified Receive will no longer be available for ${siteLabel} ${siteList} as ${pronoun} to Boost.`;
};

const buildBlockMessage = (): string => {
  return 'Unified Receive is no longer available for this site. This site has migrated to Boost.';
};

/**
 * Normalizes a site name to match the format used by SiteInput.
 * SiteInput strips a leading zero after the 3-char cluster prefix
 * (e.g., IAD057 → IAD57). We need to match both formats.
 */
const normalizeSiteName = (site: string): string => {
  if (site.length > 3 && site[3] === '0') {
    return site.substring(0, 3) + site.substring(4);
  }
  return site;
};

const matchesDeprecatedSite = (site: string, deprecatedSites: string[]): boolean => {
  const normalized = normalizeSiteName(site);
  return deprecatedSites.some(
    (ds) => ds === site || ds === normalized || normalizeSiteName(ds) === normalized,
  );
};

/**
 * Returns the deprecation status for a given site.
 * Uses the browser's current date to determine warning vs blocked state.
 *
 * Risk note: This relies on the user's device clock. If the device time
 * is incorrect, the wrong state may be shown.
 */
export const getSiteDeprecationStatus = (
  siteValue: string | undefined | null,
  now: Date = new Date(),
): SiteDeprecationResult => {
  if (!siteValue) {
    return { status: 'none', dismissible: true, message: '' };
  }

  const site = siteValue.toUpperCase();

  // First pass: check for an exact site match (highest priority)
  for (const dep of SITE_DEPRECATIONS) {
    const isDeprecatedSite = matchesDeprecatedSite(site, dep.sites);

    if (!isDeprecatedSite) continue;

    if (now >= dep.cutoffDate) {
      return {
        status: 'blocked',
        dismissible: false,
        message: buildBlockMessage(),
        boostLink: BOOST_RECEIVE_LINK,
      };
    }

    if (now >= dep.warningDate) {
      return {
        status: 'warning',
        dismissible: false,
        message: buildWarningMessage(dep),
        boostLink: BOOST_RECEIVE_LINK,
      };
    }

    // Site has a deprecation entry but dates haven't started yet
    return { status: 'none', dismissible: true, message: '' };
  }

  // Second pass: check if the site is in a cluster with active deprecations (dismissible warning)
  for (const dep of SITE_DEPRECATIONS) {
    const isInCluster = site.startsWith(dep.cluster);

    if (!isInCluster) continue;

    if (now >= dep.warningDate && now < dep.cutoffDate) {
      return {
        status: 'warning',
        dismissible: true,
        message: buildWarningMessage(dep),
        boostLink: BOOST_RECEIVE_LINK,
      };
    }
  }

  return { status: 'none', dismissible: true, message: '' };
};

/**
 * Checks if a container ID starts with a blocked (cutover) site prefix.
 * Container IDs typically start with the site name followed by a "." delimiter
 * (e.g., IAD57.BIN.001). Uses a word boundary check before "." to avoid
 * false positives (e.g., IAD579.BIN.001 should not match IAD57).
 * Returns the blocked site name if found, or null if the container is allowed.
 */
export const getBlockedSiteFromContainerId = (
  containerId: string | undefined | null,
  now: Date = new Date(),
): string | null => {
  if (!containerId) return null;

  const upperContainerId = containerId.toUpperCase();

  for (const dep of SITE_DEPRECATIONS) {
    if (now < dep.cutoffDate) continue;

    for (const site of dep.sites) {
      const normalized = normalizeSiteName(site);
      // Check both zero-padded and unpadded forms as prefix with "." boundary
      if (matchesSitePrefix(upperContainerId, site) || matchesSitePrefix(upperContainerId, normalized)) {
        return site;
      }
    }
  }

  return null;
};


/**
 * Checks if a container ID starts with a site prefix followed by a "." delimiter
 * or is exactly the site name. This prevents false positives where a longer site
 * name shares the same prefix (e.g., IAD579 should not match IAD57).
 */
const matchesSitePrefix = (containerId: string, sitePrefix: string): boolean => {
  if (!containerId.startsWith(sitePrefix)) return false;
  // Exact match or followed by "." delimiter
  return containerId.length === sitePrefix.length || containerId[sitePrefix.length] === '.';
};
